import { FormEvent, useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import style from "./style.module.css";
import { AiOutlineCloseCircle } from "react-icons/ai";
import useAnalysisTypePreferences, { IPreferences } from "../../../context/AnalysisTypePreferencesContext";
import { isMobile } from "react-device-detect";
import { useTour } from "@reactour/tour";
import useGeneralContext from "../../../context/GeneralContext";
import { eInvestorType } from "../../../@types/Users";

const AnalysisTypeSelectorDialog = ({ investorType }: { investorType: number }) => {

    const { isOpen, setIsOpen: setIsOpenPreferences, preferences, setPreferences, setOpenTour } = useAnalysisTypePreferences();
    const { tourStart, setShowTourButtons, setDisablePrev } = useGeneralContext();
    const { setIsOpen, setSteps } = useTour();
    const [checked, setChecked] = useState<IPreferences>({
        technical: false,
        fundamental: false,
        health: false,
        extra: false
    });
    const [disabled, setDisabled] = useState<boolean>(false);

    useEffect(() => {
        const hasPreferences = !!localStorage.getItem("analysis_type_preferences");
        if (!hasPreferences && !tourStart) {
            setChecked(setDefaultCheckedOptions(investorType));
            if (setSteps) {
                setSteps([{
                    selector: "#analysis-type-preferences",
                    content: "Você pode ajustar suas preferências mais tarde clicando neste botão.",
                    action: () => {
                      setShowTourButtons(true);
                      setDisablePrev(true);  
                    },
                    actionAfter: () => {
                        setIsOpen(false);
                        setSteps([]);
                        setOpenTour(false);
                        document.body.classList.remove("body-overflow");
                        document
                            .querySelector("#root > .container")
                            ?.classList.remove("disable-click");
                        document
                            .querySelector("#root .container")
                            ?.classList.remove("disable-click");
                    }
                }]);
            }
        } else {
            setChecked({
                technical: preferences?.technical,
                fundamental: preferences?.fundamental,
                health: preferences?.health,
                extra: preferences?.extra
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [investorType, isOpen]);

    function handleSubmit(e: FormEvent) {
        e.preventDefault();

        const target = e.target as typeof e.target & {
            technical: { checked: boolean };
            fundamental: { checked: boolean };
            health: { checked: boolean };
            extra: { checked: boolean };
        };

        const { technical, fundamental, health, extra } = target;

        const obj = {
            technical: technical.checked,
            fundamental: fundamental.checked,
            health: health.checked,
            extra: extra.checked
        };

        if (Object.entries(obj).every(([key, value]) => value === false)) {
            setDisabled(true);
            return;
        }

        if (!localStorage.getItem("analysis_type_preferences")) {
            setOpenTour(true);
            document
                .querySelector("#root > .container")
                ?.classList.add("disable-click");
            document
                .querySelector("#root .container")
                ?.classList.add("disable-click");
        }

        localStorage.setItem("analysis_type_preferences", JSON.stringify(obj));
        setIsOpenPreferences(false);

        setPreferences(obj);
    }

    function handleChange(preferenceKey: keyof IPreferences) {
        setChecked((prev) => ({ ...prev, [preferenceKey]: !prev[preferenceKey] }));
        setDisabled(false);
    }

    function handleHideModal() {
        setIsOpenPreferences(false);
        document.body.classList.remove("body-overflow");
    }

    if (!isOpen) {
        return null;
    }

    return (
        <Modal show={isOpen} onHide={() => handleHideModal()} centered={isMobile}>
            <div className={style.container}>
                <button type="button" className={style.close} onClick={() => handleHideModal()}>
                    <AiOutlineCloseCircle color="var(--text-1)" size={25}
                    />
                </button>
                <h1 className={style.title}>Preferências</h1>
                <p>Selecione quais análises você deseja levar em consideração.</p>
                <form className={style.form} onSubmit={handleSubmit}>
                    <label>
                        <input type="checkbox" name="fundamental" id="fundamental" checked={checked.fundamental} onChange={() => handleChange("fundamental")} />
                        Análise fundamentalista
                    </label>
                    <label>
                        <input type="checkbox" name="technical" id="technical" checked={checked.technical} onChange={() => handleChange("technical")} />
                        Análise técnica
                    </label>
                    <label>
                        <input type="checkbox" name="health" id="health" checked={checked.health} onChange={() => handleChange("health")} />
                        Saúde da empresa
                    </label>
                    <label>
                        <input type="checkbox" name="extra" id="extra" checked={checked.extra} onChange={() => handleChange("extra")} />
                        Avaliações importantes
                    </label>
                    {
                        disabled && <p className={style.errorMessage}>Selecione pelo menos uma das opções.</p>
                    }
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={disabled}
                        >
                        Salvar alterações
                    </button>
                </form>
            </div>
        </Modal>
    );
}

export default AnalysisTypeSelectorDialog;

function setDefaultCheckedOptions(investorType: number): IPreferences {
    switch (investorType) {
        case eInvestorType.DAY_TRADER:
            return {
                technical: true,
                fundamental: false,
                health: false,
                extra: true
            };
        case eInvestorType.SWING_TRADER:
            return {
                technical: true,
                fundamental: true,
                health: true,
                extra: true
            };
        case eInvestorType.BUY_AND_HOLDER:
            return {
                technical: false,
                fundamental: true,
                health: true,
                extra: true
            };
        default:
            return {
                technical: true,
                fundamental: true,
                health: true,
                extra: true
            };
    }
}